<template>
  <div class="items-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="inventory" color="secondary" class="q-mr-sm" />
            {{ title }}
          </div>
          <div class="panel-subtitle">
            {{ subtitle }}
          </div>
        </div>
        <div class="col-auto">
          <q-badge
            :color="availableItemsCount > 0 ? 'warning' : 'positive'"
            :outline="availableItemsCount === 0"
            class="q-pa-sm text-bold"
          >
            <template v-if="availableItemsCount > 0">
              <q-icon name="warning" class="q-mr-xs" />
              {{ availableItemsCount }} unallocated item{{ availableItemsCount > 1 ? 's' : '' }}
            </template>
            <template v-else>
              <q-icon name="check_circle" class="q-mr-xs" />
              All items allocated
            </template>
          </q-badge>
        </div>
      </div>
    </div>

    <!-- Search and Filter Controls -->
    <div class="panel-controls">
      <q-input
        v-model="localItemSearch"
        label="Search items"
        clearable
        filled
        dense
      >
        <template #prepend>
          <q-icon name="search" />
        </template>
      </q-input>

      <div class="controls-row">
        <q-select
          v-model="localItemTypeFilter"
          :options="itemTypeOptions"
          label="Filter by type"
          filled
          dense
          emit-value
          map-options
          class="filter-select"
        />

        <!-- Selection Controls -->
        <div class="selection-controls">
          <q-btn
            v-if="selectedItems.length > 0"
            color="primary"
            icon="add"
            :label="`Add ${selectedItems.length}`"
            size="sm"
            unelevated
            @click="addSelectedItems"
          />
          <q-btn
            v-if="filteredItems.length > 0"
            :color="allSelected ? 'grey' : 'secondary'"
            :icon="allSelected ? 'deselect' : 'select_all'"
            :label="allSelected ? 'Clear' : 'Select All'"
            size="sm"
            flat
            @click="toggleSelectAll"
          />
        </div>
      </div>
    </div>

    <!-- Items List -->
    <div
      ref="itemsListRef"
      class="items-list"
      @mousedown="onMouseDown"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
      @mouseleave="onMouseUp"
    >
      <div v-if="!filteredItems.length" class="empty-state">
        <q-icon name="inventory" size="2em" color="grey-5" />
        <div class="empty-title">{{ items.length ? 'No matching items found' : 'Loading items...' }}</div>
        <div v-if="items.length" class="empty-subtitle">
          Try adjusting your search or filter criteria
        </div>
      </div>

      <div v-else class="items-grid">
        <div
          v-for="(item, index) in filteredItems"
          :key="item.id"
          :ref="el => setItemRef(el, index)"
          class="compact-item"
          :class="{
            'item-selected': isSelected(item),
            'item-in-marquee': isInMarquee(index)
          }"
          @click="handleItemClick($event, item)"
        >
          <q-checkbox
            :model-value="isSelected(item)"
            size="sm"
            class="item-checkbox"
            @click.stop
            @update:model-value="toggleSelection(item)"
          />
          <div class="item-info">
            <div class="item-name">{{ getItemName(item) }}</div>
            <div class="item-details-row">
              <span v-if="item.part_number" class="part-number">{{ item.part_number }}</span>
              <span class="item-price">{{ item.quantity }} × ${{ formatPrice(item.unit_price) }}</span>
              <q-badge
                v-if="getItemType(item)"
                :color="getTypeColor(item)"
                size="sm"
              >
                {{ getItemType(item) }}
              </q-badge>
            </div>
          </div>
          <q-btn
            flat
            round
            color="primary"
            icon="add"
            size="sm"
            class="add-btn"
            @click.stop="handleAddItem(item)"
          >
            <q-tooltip>Add to section</q-tooltip>
          </q-btn>
        </div>
      </div>

      <!-- Selection Marquee -->
      <div
        v-if="isDragging"
        class="selection-marquee"
        :style="marqueeStyle"
      ></div>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, watch, onBeforeUpdate } from 'vue';

  const props = defineProps({
    items: {
      type: Array,
      default: () => [],
    },
    itemSearch: {
      type: String,
      default: '',
    },
    itemTypeFilter: {
      type: String,
      default: 'all',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Available Items',
    },
    subtitle: {
      type: String,
      default: 'Items that can be added to this section',
    },
  });

  const emit = defineEmits(['add-item', 'add-items', 'update:itemSearch', 'update:itemTypeFilter']);

  // Local state for search and filter (synced with props)
  const localItemSearch = ref(props.itemSearch);
  const localItemTypeFilter = ref(props.itemTypeFilter);
  const selectedItems = ref([]);

  // Marquee selection state
  const itemsListRef = ref(null);
  const itemRefs = ref([]);
  const isDragging = ref(false);
  const dragStart = ref({ x: 0, y: 0 });
  const dragCurrent = ref({ x: 0, y: 0 });
  const marqueeIndices = ref(new Set());
  const clickedWithoutDrag = ref(false);

  // Reset item refs before each update
  onBeforeUpdate(() => {
    itemRefs.value = [];
  });

  function setItemRef(el, index) {
    if (el) {
      itemRefs.value[index] = el;
    }
  }

  // Watch props and sync to local state
  watch(() => props.itemSearch, (newVal) => {
    localItemSearch.value = newVal;
  });

  watch(() => props.itemTypeFilter, (newVal) => {
    localItemTypeFilter.value = newVal;
  });

  // Watch local state and emit updates
  watch(localItemSearch, (newVal) => {
    emit('update:itemSearch', newVal);
  });

  watch(localItemTypeFilter, (newVal) => {
    emit('update:itemTypeFilter', newVal);
  });

  // Clear selection when items change (e.g., after adding items)
  watch(() => props.items, () => {
    // Remove any selected items that are no longer in the list
    selectedItems.value = selectedItems.value.filter((selected) =>
      props.items.some((item) => item.id === selected.id),
    );
  }, { deep: true });

  // Computed
  const itemTypeOptions = computed(() => [
    { label: 'All Types', value: 'all' },
    { label: 'Product', value: 'product' },
    { label: 'Labor', value: 'labor' },
    { label: 'Supplier Quote', value: 'supplier_quote' },
    { label: 'Contractor Quote', value: 'contractor_quote' },
  ]);

  const availableItemsCount = computed(() => {
    // If there's a parts group, count its items plus other available items
    const partsGroup = props.items.find((item) => item.id === 'parts_group' && item.is_group);
    if (partsGroup) {
      // Count individual parts in the group + other non-part items
      const nonPartsItems = props.items.filter((item) => item.id !== 'parts_group').length;
      return (partsGroup.item_ids?.length || 0) + nonPartsItems;
    }

    // Otherwise just count the total available items
    return props.items.length;
  });

  const filteredItems = computed(() => {
    if (!props.items.length) {return [];}

    let filtered = [...props.items];

    // Apply search filter
    if (localItemSearch.value && localItemSearch.value.trim()) {
      const search = localItemSearch.value.toLowerCase().trim();
      filtered = filtered.filter((item) => {
        const description = item.description ? item.description.toLowerCase() : '';
        const partNumber = item.part_number ? item.part_number.toLowerCase() : '';
        const name = item.name ? item.name.toLowerCase() : '';
        return description.includes(search) || partNumber.includes(search) || name.includes(search);
      });
    }

    // Apply type filter
    if (localItemTypeFilter.value && localItemTypeFilter.value !== 'all') {
      filtered = filtered.filter((item) => {
        // Check multiple ways to determine item type
        if (localItemTypeFilter.value === 'product' || localItemTypeFilter.value === 'parts') {
          // For products/parts - check either type
          return (
            item.type === 'product' ||
            item.type === 'parts' ||
            item.category === 'product' ||
            item.category === 'parts' ||
            (item.is_group && item.type === 'parts')
          );
        } else if (localItemTypeFilter.value === 'labor' || localItemTypeFilter.value === 'contractor_quote') {
          // For labor/contractor quotes - more comprehensive check for labor items
          return (
            item.type === 'labor' ||
            item.type === 'subcontractor_quote' ||
            item.category === 'labor' ||
            item.item_type === 'labor' ||
            item.fromLabor === true ||
            item.isLabor === true ||
            (item.id && String(item.id).startsWith('labor_')) ||
            // Check for labor_id property
            (item.labor_id && item.labor_id > 0) ||
            // Check for numeric_id which we use for labor items
            (item.numeric_id && item.original_id && String(item.original_id).startsWith('labor_'))
          );
        } else if (localItemTypeFilter.value === 'supplier_quote') {
          // For supplier quotes
          return (
            item.type === 'supplier_quote' ||
            item.category === 'supplier_quote' ||
            item.fromSupplierQuote === true
          );
        }

        // Default to simple type comparison
        return item.type === localItemTypeFilter.value;
      });
    }

    return filtered;
  });

  const allSelected = computed(() => {
    if (filteredItems.value.length === 0) {return false;}
    return filteredItems.value.every((item) => isSelected(item));
  });

  // Marquee style computed
  const marqueeStyle = computed(() => {
    if (!isDragging.value) {return {};}

    const left = Math.min(dragStart.value.x, dragCurrent.value.x);
    const top = Math.min(dragStart.value.y, dragCurrent.value.y);
    const width = Math.abs(dragCurrent.value.x - dragStart.value.x);
    const height = Math.abs(dragCurrent.value.y - dragStart.value.y);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    };
  });

  // Methods
  function isSelected(item) {
    return selectedItems.value.some((s) => s.id === item.id);
  }

  function isInMarquee(index) {
    return marqueeIndices.value.has(index);
  }

  function toggleSelection(item) {
    const index = selectedItems.value.findIndex((s) => s.id === item.id);
    if (index >= 0) {
      selectedItems.value.splice(index, 1);
    } else {
      selectedItems.value.push(item);
    }
  }

  function handleItemClick(event, item) {
    // Only toggle if it was a click without drag
    if (clickedWithoutDrag.value) {
      toggleSelection(item);
    }
    clickedWithoutDrag.value = false;
  }

  // Marquee selection handlers
  function onMouseDown(event) {
    // Ignore if clicking on interactive elements
    if (event.target.closest('.q-checkbox, .q-btn, .add-btn')) {
      return;
    }

    const container = itemsListRef.value;
    if (!container) {return;}

    const rect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    dragStart.value = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top + scrollTop,
    };
    dragCurrent.value = { ...dragStart.value };
    clickedWithoutDrag.value = true;
  }

  function onMouseMove(event) {
    if (!clickedWithoutDrag.value && !isDragging.value) {return;}

    const container = itemsListRef.value;
    if (!container) {return;}

    const rect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    const currentX = event.clientX - rect.left;
    const currentY = event.clientY - rect.top + scrollTop;

    // Check if we've moved enough to start dragging
    const dx = Math.abs(currentX - dragStart.value.x);
    const dy = Math.abs(currentY - dragStart.value.y);

    if (clickedWithoutDrag.value && (dx > 5 || dy > 5)) {
      // Start dragging
      isDragging.value = true;
      clickedWithoutDrag.value = false;
      marqueeIndices.value = new Set();
    }

    if (isDragging.value) {
      dragCurrent.value = { x: currentX, y: currentY };
      updateMarqueeSelection();
    }
  }

  function onMouseUp() {
    if (isDragging.value) {
      // Apply marquee selection
      marqueeIndices.value.forEach((index) => {
        const item = filteredItems.value[index];
        if (item && !isSelected(item)) {
          selectedItems.value.push(item);
        }
      });
      marqueeIndices.value = new Set();
    }
    isDragging.value = false;
  }

  function updateMarqueeSelection() {
    if (!itemsListRef.value) {return;}

    const container = itemsListRef.value;
    const containerRect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    // Get marquee bounds
    const marqueeLeft = Math.min(dragStart.value.x, dragCurrent.value.x);
    const marqueeRight = Math.max(dragStart.value.x, dragCurrent.value.x);
    const marqueeTop = Math.min(dragStart.value.y, dragCurrent.value.y);
    const marqueeBottom = Math.max(dragStart.value.y, dragCurrent.value.y);

    const newIndices = new Set();

    itemRefs.value.forEach((el, index) => {
      if (!el) {return;}

      const itemRect = el.getBoundingClientRect();

      // Convert item rect to container-relative coordinates
      const itemLeft = itemRect.left - containerRect.left;
      const itemRight = itemRect.right - containerRect.left;
      const itemTop = itemRect.top - containerRect.top + scrollTop;
      const itemBottom = itemRect.bottom - containerRect.top + scrollTop;

      // Check for intersection
      const intersects =
        itemLeft < marqueeRight &&
        itemRight > marqueeLeft &&
        itemTop < marqueeBottom &&
        itemBottom > marqueeTop;

      if (intersects) {
        newIndices.add(index);
      }
    });

    marqueeIndices.value = newIndices;
  }

  function toggleSelectAll() {
    if (allSelected.value) {
      // Deselect all filtered items
      selectedItems.value = selectedItems.value.filter(
        (s) => !filteredItems.value.some((f) => f.id === s.id),
      );
    } else {
      // Select all filtered items
      filteredItems.value.forEach((item) => {
        if (!isSelected(item)) {
          selectedItems.value.push(item);
        }
      });
    }
  }

  function addSelectedItems() {
    if (selectedItems.value.length > 0) {
      // Emit all selected items
      selectedItems.value.forEach((item) => {
        emit('add-item', item);
      });
      selectedItems.value = [];
    }
  }

  function handleAddItem(item) {
    emit('add-item', item);
    // Remove from selection if it was selected
    const index = selectedItems.value.findIndex((s) => s.id === item.id);
    if (index >= 0) {
      selectedItems.value.splice(index, 1);
    }
  }

  function getItemName(item) {
    return item.description || item.product_name || item.name || item.part_number || 'Unnamed Item';
  }

  function getItemType(item) {
    if (item.is_labor || item.item_type === 'labor' || item.type === 'labor' || item.fromLabor) {
      const laborType = item.labor_type ||
        item.metadata?.original_labor_type ||
        item.metadata?.laborType ||
        'Labor';
      return laborType.replace(/_/g, ' ').split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
    }
    if (item.type && item.type !== 'labor') {
      return item.type.charAt(0).toUpperCase() + item.type.slice(1);
    }
    return null;
  }

  function getTypeColor(item) {
    if (item.is_labor || item.item_type === 'labor' || item.type === 'labor' || item.fromLabor) {
      return 'accent';
    }
    return 'secondary';
  }

  function formatPrice(value) {
    const num = parseFloat(value || 0);
    return num.toFixed(2);
  }
</script>

<style scoped>
.items-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.panel-header {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  flex-shrink: 0;
}

.panel-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  color: #1d1d1d;
  margin-bottom: 2px;
}

.panel-subtitle {
  font-size: 0.8rem;
  color: #6c757d;
  margin-left: 32px;
}

.panel-controls {
  padding: 12px 16px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
  flex-shrink: 0;
}

.controls-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-top: 8px;
}

.filter-select {
  flex: 1;
  min-width: 120px;
}

.selection-controls {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.items-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  position: relative;
  user-select: none;
}

.items-grid {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Compact Item Row */
.compact-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  gap: 8px;
}

.compact-item:hover {
  background: #f5f5f5;
  border-color: #1976d2;
}

.compact-item.item-selected {
  background: #e3f2fd;
  border-color: #1976d2;
}

.compact-item.item-in-marquee {
  background: #bbdefb;
  border-color: #1976d2;
}

.item-checkbox {
  flex-shrink: 0;
}

.item-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.item-name {
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-details-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  font-size: 11px;
}

.part-number {
  color: #616161;
  font-family: monospace;
}

.item-price {
  color: #424242;
}

.add-btn {
  flex-shrink: 0;
  opacity: 0.7;
}

.compact-item:hover .add-btn {
  opacity: 1;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  text-align: center;
}

.empty-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: #495057;
  margin-top: 12px;
}

.empty-subtitle {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 4px;
}

/* Selection Marquee */
.selection-marquee {
  position: absolute;
  background: rgba(25, 118, 210, 0.15);
  border: 2px dashed #1976d2;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10;
}
</style>
